// Field total from the lab pricing tier. The rate is `lab_pricing_tiers.price_per_acre`,
// served by POST /api/soil-sampling/quote. This multiply stays here so the total updates
// as acres change without another request. Half-up to cents, same as the quote.

// Shortest decimal spelling, so 10.1 × 1.15 is the digits 101 and 115. A binary
// multiply makes that product 11.614999999999998, and Math.round then yields 11.61.
function decimalDigits(
  value: number,
): { digits: bigint; scale: number } | null {
  if (!Number.isFinite(value)) return null;
  const negative = value < 0;
  let text = Math.abs(value).toString();
  if (/[eE]/.test(text))
    text = Math.abs(value)
      .toFixed(12)
      .replace(/\.?0+$/, "");
  const [whole, frac = ""] = text.split(".");
  const body = `${whole}${frac}`.replace(/^0+(?=\d)/, "");
  return {
    digits: BigInt(body || "0") * (negative ? -1n : 1n),
    scale: frac.length,
  };
}

function mulHalfUpCents(left: number, right: number): number | null {
  const a = decimalDigits(left);
  const b = decimalDigits(right);
  if (!a || !b) return null;
  const negative = a.digits < 0n !== b.digits < 0n;
  const product =
    (a.digits < 0n ? -a.digits : a.digits) *
    (b.digits < 0n ? -b.digits : b.digits);
  const scale = a.scale + b.scale;
  let cents: bigint;
  if (scale <= 2) {
    cents = product * 10n ** BigInt(2 - scale);
  } else {
    const factor = 10n ** BigInt(scale - 2);
    cents = product / factor;
    if ((product % factor) * 2n >= factor) cents += 1n;
  }
  const signed = negative ? -cents : cents;
  return Number(signed) / 100;
}

export function fieldTotalPrice(
  acres: number,
  pricePerAcre: number | null | undefined,
): number | null {
  if (!(acres > 0) || pricePerAcre == null || !Number.isFinite(pricePerAcre))
    return null;
  return mulHalfUpCents(acres, pricePerAcre);
}

export function pricePerAcreOf(tier: unknown): number | null {
  if (!tier || typeof tier !== "object") return null;
  const raw = (tier as { price_per_acre?: unknown }).price_per_acre;
  if (raw == null || raw === "") return null;
  const rate = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(rate) ? rate : null;
}
